// SkinExpert/src/app/layout/account/TeamManagement.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AccountService, { TeamMember } from "../../services/AccountService";
import dayjs from "dayjs";

export default function TeamManagement() {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [maxMembers, setMaxMembers] = useState(2);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  
  
  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<TeamMember | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadTeamMembers();
  }, []);

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await AccountService.getTeamMembers();
      setTeamMembers(data.teamMembers);
      setMaxMembers(data.maxMembers);
    } catch (err: any) {
      setError(err.message || "Failed to load team members");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeamMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!createForm.email || !createForm.password) {
      setError("Email and password are required");
      return;
    }

    if (createForm.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    try {
      setCreating(true);
      const result = await AccountService.createTeamMember({
        email: createForm.email,
        password: createForm.password,
      });
      
      setSuccess(result.message);
      setCreateDialogOpen(false);
      setCreateForm({ email: "", password: "" });
      await loadTeamMembers();
    } catch (err: any) {
      setError(err.message || "Failed to create team member");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTeamMember = async () => {
    if (!memberToDelete) return;

    try {
      setDeleting(true);
      setError(null);
      const result = await AccountService.deleteTeamMember(memberToDelete.id);
      setSuccess(result.message);
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
      await loadTeamMembers();
    } catch (err: any) {
      setError(err.message || "Failed to delete team member");
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (member: TeamMember) => {
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box maxWidth="900px" mx="auto">
      <Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <PeopleIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 0.5 }}>
              Team Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add up to {maxMembers} nurse users who share your subscription
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          disabled={teamMembers.length >= maxMembers}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            px: 3,
          }}
        >
          Add Team Member
        </Button>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ mb: 3, borderRadius: 2 }} 
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Card sx={{ borderRadius: 3, boxShadow: "0 2px 10px rgba(0,0,0,0.08)" }}>
        <CardContent>
          <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Team Members ({teamMembers.length}/{maxMembers})
            </Typography>
            {teamMembers.length >= maxMembers && (
              <Alert severity="info" sx={{ borderRadius: 2, py: 0.5 }}>
                Maximum team members reached
              </Alert>
            )}
          </Box>

          {teamMembers.length === 0 ? (
            <Box 
              textAlign="center" 
              py={6}
              sx={{
                bgcolor: "background.default",
                borderRadius: 2,
                border: "2px dashed",
                borderColor: "divider",
              }}
            >
              <PeopleIcon sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No team members yet
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Add nurse users to share your subscription and collaborate
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
              >
                Add Your First Team Member
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamMembers.map((member) => (
                    <TableRow key={member.id} hover>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>{member.userName || "—"}</TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            textTransform: "capitalize",
                            fontWeight: 500,
                            color: "primary.main"
                          }}
                        >
                          {member.planType || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            textTransform: "capitalize",
                            color: member.subscriptionStatus === "active" ? "success.main" : "text.secondary",
                            fontWeight: 500,
                          }}
                        >
                          {member.subscriptionStatus || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {member.createdAt 
                          ? dayjs(member.createdAt).format("MMM D, YYYY")
                          : "—"}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Delete team member">
                          <IconButton
                            onClick={() => openDeleteDialog(member)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create Team Member Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setCreateForm({ email: "", password: "" });
          setShowPassword(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Add Team Member
          </Typography>
        </DialogTitle>
        <form onSubmit={handleCreateTeamMember}>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Create a nurse account that will share your subscription and access your clinic's resources.
            </Typography>
            
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              required
              margin="normal"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              required
              margin="normal"
              helperText="Minimum 6 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />

            <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
              This user will have "nurse" role and full access to your subscription features.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => {
                setCreateDialogOpen(false);
                setCreateForm({ email: "", password: "" });
                setShowPassword(false);
              }}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={creating}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
              }}
            >
              {creating ? <CircularProgress size={24} /> : "Create Team Member"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setMemberToDelete(null);
        }}
        maxWidth="sm"
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete team member <strong>{memberToDelete?.email}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setMemberToDelete(null);
            }}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTeamMember}
            variant="contained"
            color="error"
            disabled={deleting}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            {deleting ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
